import * as React from 'react';
import { Grid, Box, Typography, Chip } from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import AirIcon from '@mui/icons-material/Air';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptIcon from '@mui/icons-material/Receipt';

// Modern stil tanımları
const styles = {
  container: {
    backgroundColor: '#f8fafc',
    borderRadius: '16px',
    padding: '16px',
    border: '1px solid #e2e8f0',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    marginBottom: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e2e8f0',
  },
  sectionIcon: {
    width: 40,
    height: 40,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#1e293b',
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    marginLeft: '52px',
    marginBottom: '4px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #f1f5f9',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8fafc',
      borderColor: '#e2e8f0',
    },
  },
  detailLabel: {
    fontSize: '0.85rem',
    color: '#64748b',
    fontWeight: 500,
  },
  countChip: {
    fontWeight: 600,
    fontSize: '0.8rem',
    minWidth: '32px',
    height: '24px',
    borderRadius: '6px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    color: '#cbd5e1',
    marginBottom: '12px',
  },
};

// İkon ve renk eşleştirmesi
const sectionConfig = {
  'Merkezi Kumanda': {
    icon: <SettingsIcon />,
    bgColor: '#eff6ff',
    iconColor: '#2563eb',
    chipColor: '#dbeafe',
    chipTextColor: '#1d4ed8',
  },
  'Isı Pompası': {
    icon: <DeviceThermostatIcon />,
    bgColor: '#fef3c7',
    iconColor: '#d97706',
    chipColor: '#fef3c7',
    chipTextColor: '#b45309',
  },
  'Taze Hava': {
    icon: <AirIcon />,
    bgColor: '#dcfce7',
    iconColor: '#16a34a',
    chipColor: '#dcfce7',
    chipTextColor: '#15803d',
  },
  'PDI': {
    icon: <ReceiptIcon />,
    bgColor: '#f3e8ff',
    iconColor: '#9333ea',
    chipColor: '#f3e8ff',
    chipTextColor: '#7e22ce',
  },
  'default': {
    icon: <AcUnitIcon />,
    bgColor: '#e0f2fe',
    iconColor: '#0284c7',
    chipColor: '#e0f2fe',
    chipTextColor: '#0369a1',
  },
};

const getConfig = (name) => {
  return sectionConfig[name] || sectionConfig['default'];
};

export default function ProjectSummaryList(props) {
  const { data } = props;
  const { formData } = props;
  const level0Items = [
    { Id: 0, SystemName: "Merkezi Kumanda", Details: [] },
    { Id: 0, SystemName: "Isı Pompası", Details: [] },
    { Id: 0, SystemName: "Taze Hava", Details: [] },
    { Id: 0, SystemName: "PDI", Details: [] }
  ];

  if (parseInt(formData.ControlRoomCount, 10) > 0) {
    level0Items[0].Details.push({ Name: "Merkezi Kumanda Fiş Adet", Count: parseInt(formData.ControlRoomCount, 10) });
  }
  
  if (parseInt(formData.HeatPumpCount, 10) > 0) {
    level0Items[1].Details.push({ Name: "Isı Pompası Fiş Adet", Count: parseInt(formData.HeatPumpCount, 10) });
  }
  
  if (parseInt(formData.FreshAirCount, 10) > 0) {
    level0Items[2].Details.push({ Name: "Taze Hava Fiş Adet", Count: parseInt(formData.FreshAirCount, 10) });
  }
  
  if (parseInt(formData.PdiCount, 10) > 0) {
    level0Items[3].Details.push({ Name: "PDI Fiş Adet", Count: parseInt(formData.PdiCount, 10) });
  }

  const level1Items = data
  .filter(item => item.Level === 1)
  .map(
    ({
      Id,
      SystemName,
      OduCount,
      IduCount,
      HydrocideCount,
      RemoteCount,
      FreshAirDxCount,
      FrontPanelCount,
      HrBoxCount,
      IntegrationModuleCount,
      KsCommunicationKitCount,
      EevKitCount,
      FdxBoxCount,
      AirRouterCount
    }) => ({
      Id,
      SystemName,
      Details: [
        { Id: Id, Name: "ODU Fiş Adet", Count: parseInt(OduCount, 10) || 0 },
        { Id: Id, Name: "IDU Fiş Adet", Count: parseInt(IduCount, 10) || 0 },
        { Id: Id, Name: "Hydrocide Fiş Adet", Count: parseInt(HydrocideCount, 10) || 0 },
        { Id: Id, Name: "Remote Fiş Adet", Count: parseInt(RemoteCount, 10) || 0 },
        { Id: Id, Name: "Fresh Air DX Fiş Adet", Count: parseInt(FreshAirDxCount, 10) || 0 },
        { Id: Id, Name: "Front Panel Fiş Adet", Count: parseInt(FrontPanelCount, 10) || 0 },
        { Id: Id, Name: "HR Box Fiş Adet", Count: parseInt(HrBoxCount, 10) || 0 },
        { Id: Id, Name: "Entegrasyon Modül Fiş Adet", Count: parseInt(IntegrationModuleCount, 10) || 0 },
        { Id: Id, Name: "KS Haberleşme Kit Fiş Adet", Count: parseInt(KsCommunicationKitCount, 10) || 0 },
        { Id: Id, Name: "EEV Kit Fiş Adet", Count: parseInt(EevKitCount, 10) || 0 },
        { Id: Id, Name: "FDX Box Fiş Adet", Count: parseInt(FdxBoxCount, 10) || 0 },
        { Id: Id, Name: "Hava Yönlendirici Fiş Adet", Count: parseInt(AirRouterCount, 10) || 0 }
      ].filter(detail => detail.Count > 0)
    })
  );

  const headerData = [...level0Items, ...level1Items].filter(
    item => item.Details && item.Details.length > 0
  );

  if (headerData.length === 0) {
    return (
      <Box sx={styles.emptyState}>
        <ReceiptIcon sx={styles.emptyIcon} />
        <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
          Henüz proje özeti bulunmuyor
        </Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '4px' }}>
          Adetleri girdikten sonra özet görüntülenecek
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container>
      <Grid size={12}>
        <Box sx={styles.container}>
          {headerData.map((header, headerIndex) => {
            const config = getConfig(header.SystemName);
            return (
              <Box key={`section-${header.Id}-${headerIndex}`} sx={{ marginBottom: '16px' }}>
                {/* Section Header */}
                <Box sx={styles.sectionHeader}>
                  <Box sx={{ 
                    ...styles.sectionIcon, 
                    backgroundColor: config.bgColor,
                    color: config.iconColor 
                  }}>
                    {config.icon}
                  </Box>
                  <Typography sx={styles.sectionTitle}>
                    {header.SystemName}
                  </Typography>
                  <Chip 
                    label={`${header.Details.length} kalem`}
                    size="small"
                    sx={{
                      marginLeft: 'auto',
                      backgroundColor: '#f1f5f9',
                      color: '#64748b',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                    }}
                  />
                </Box>
                
                {/* Detail Items */}
                {header.Details.map((item, itemIndex) => (
                  <Box
                    key={`item-${item.Id}-${itemIndex}`}
                    sx={styles.detailItem}
                  >
                    <Typography sx={styles.detailLabel}>
                      {item.Name}
                    </Typography>
                    <Chip 
                      label={item.Count}
                      size="small"
                      sx={{
                        ...styles.countChip,
                        backgroundColor: config.chipColor,
                        color: config.chipTextColor,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      </Grid>
    </Grid>
  );
}
